import { Link, useParams } from "react-router-dom";
import { usePartnerById, usePartnerMembers } from "../../hooks/use-partner";
import { useMemo, useState } from "react";
import BulkEnrollModal from "../../components/bulk-enroll-modal";
import Shell from "../../components/shell";
import DataTable from "../../components/tables";

export default function PartnerMembers() {
  const { partnerId } = useParams();
  const { data: partnerRes, isLoading: loadingPartner } =
    usePartnerById(partnerId);
  const { data: membersRes, isLoading: loadingMembers } =
    usePartnerMembers(partnerId);
  const [openBulk, setOpenBulk] = useState(false);

  const partner = partnerRes?.data || {};
  const rows = membersRes?.data?.documents || membersRes?.data || [];

  const headerBtnAction = {
    isActive: true,
    title: "Enroll Members",
    onClick: () => {
      setOpenBulk(true);
    },
  };

  const [globalFilter, setGlobalFilter] = useState("");

  const columns = useMemo(
    () => [
      {
        header: "NAME",
        accessorFn: (row) =>
          `${row.firstName || "—"} ${row.lastName || ""}`.trim(),
        cell: ({ getValue }) => (
          <span className="capitalize">{getValue()}</span>
        ),
      },
      {
        header: "EMAIL",
        accessorKey: "email",
        cell: ({ getValue }) => <span className="">{getValue() || "—"}</span>,
      },
      {
        header: "MEMBERSHIP",
        accessorKey: "membershipTitle",
        cell: ({ getValue }) => {
          const title = getValue();
          return title ? (
            <span className="inline-flex items-center rounded border px-2 py-0.5 text-xs uppercase">
              {title}
            </span>
          ) : (
            "—"
          );
        },
      },
      {
        header: "REGISTRATION NUMBER",
        accessorKey: "registrationNumber",
        cell: ({ getValue }) => {
          const title = getValue();
          return title ? (
            <span className="inline-flex items-center rounded border px-2 py-0.5 text-xs uppercase">
              {title}
            </span>
          ) : (
            "—"
          );
        },
      },
      {
        header: "ASSIGNED COURSES",
        accessorFn: (row) =>
          Array.isArray(row.courses) && row.courses.length
            ? row.courses.length
            : "—",
        cell: ({ getValue }) => <span className="uppercase">{getValue()}</span>,
      },
      {
        header: "CREATED",
        accessorKey: "createdAt",
        cell: ({ getValue }) => (
          <span className="uppercase">
            {getValue() ? new Date(getValue()).toLocaleDateString() : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const partnerName = partner.partnerName || partner.name || "Partner";
  const partnerEmail = partner.email || "";
  const partnerLocation = partner.partnerLocation || "—";
  const targetTrainees =
    typeof partner.targetTrainees === "number" ? partner.targetTrainees : "—";
  const usersCount = Array.isArray(partner.users) ? partner.users.length : 0;
  const isActive = partner.isActive === true;
  const createdAt = partner.createdAt
    ? new Date(partner.createdAt).toLocaleString()
    : "—";
  const updatedAt = partner.updatedAt
    ? new Date(partner.updatedAt).toLocaleString()
    : "—";
  const partnerObjectId = partner._id || "—";
  const enrolledCount = Array.isArray(rows) ? rows.length : 0;

  return (
    <Shell
      pageHeader="View all members"
      pageTitle="Members"
      btnAction={headerBtnAction}
    >
      <div className="p-4 md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">
              {loadingPartner ? "Loading..." : partnerName}
            </h1>
            {partnerEmail ? (
              <div className="text-sm text-slate-500">{partnerEmail}</div>
            ) : null}
          </div>
          <Link
            to="/admin/partners"
            className="rounded-md border px-3 py-2 text-sm"
          >
            ← Back
          </Link>
        </div>

        <div className="mb-6 grid gap-4 rounded-lg border bg-white p-4 md:grid-cols-12">
          <div className="md:col-span-5 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{partnerName}</h2>
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${
                  isActive
                    ? "border-emerald-300 text-emerald-700"
                    : "border-rose-300 text-rose-700"
                }`}
              >
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="text-sm text-slate-600">
              <span className="font-medium">Location:</span> {partnerLocation}
            </div>
            <div className="text-sm text-slate-600">
              <span className="font-medium">Partner ID:</span> {partnerObjectId}
            </div>
            <div className="text-xs text-slate-500">
              <span className="font-medium">Created:</span> {createdAt}
              <span className="mx-2">•</span>
              <span className="font-medium">Updated:</span> {updatedAt}
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-md border p-3">
              <div className="text-xs text-slate-500">Target Trainees</div>
              <div className="mt-1 text-2xl font-semibold">
                {targetTrainees}
              </div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs text-slate-500">Users (assigned)</div>
              <div className="mt-1 text-2xl font-semibold">{usersCount}</div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs text-slate-500">Enrolled Members</div>
              <div className="mt-1 text-2xl font-semibold">{enrolledCount}</div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs text-slate-500">Status</div>
              <div className="mt-1 text-base font-medium">
                {isActive ? "Active" : "Inactive"}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-3">
          <DataTable
            data={rows?.data?.documents || []}
            columns={columns}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            pageSize={10}
            isLoading={loadingMembers}
            noDataMessage="No members found for this partner"
          />
        </div>

        {openBulk && <BulkEnrollModal onClose={() => setOpenBulk(false)} />}
      </div>
    </Shell>
  );
}
